"use client";

import { useQuery } from "@apollo/client";
import {
	GroupedProductsDocument,
	type GroupedProductsQuery,
	type GroupedProductsQueryVariables,
} from "@graphql";
import { useFinishInputs, useProductInputs } from "@tosize/shapes/src/lib/swr";
import type { SimpleProduct } from "@tosize/shapes/src/types/product";
import { ProductionUnit, fetcher } from "@tosize/ui";
import { useSearchParams } from "next/navigation";
import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useCommand } from "~/app/[lang]/command/Context";
import type {
	FinishesType,
	ListImportFormType,
	SidesType,
} from "~/app/[lang]/command/dialogs/import/schema";

export function getGroupedProductData({ id }: { id?: string }) {
	const { activeDialog } = useCommand();

	const { data, loading } = useQuery<GroupedProductsQuery, GroupedProductsQueryVariables>(
		GroupedProductsDocument,
		{
			variables: { id: id ?? "" },
			skip:
				!id || (activeDialog !== "importProducts_list" && activeDialog !== "importProducts_file"),
		},
	);

	const products = data?.products.items
		.filter((item) => item.isSalable)
		.sort((a, b) => {
			if (a.name && b.name) {
				return a.name.localeCompare(b.name);
			}

			return 0;
		});

	return {
		products,
		isLoading: loading,
	};
}

interface SimpleProductsDataProps {
	apiUrl: string;
	groupedProductId: string;
}

export function getSimpleProductsData(props: SimpleProductsDataProps) {
	const { apiUrl, groupedProductId } = props;

	const form = useFormContext<ListImportFormType>();
	const searchParams = useSearchParams();
	const defaultSimpleProductId = searchParams.get("thickness") ?? undefined;

	const { data, isLoading } = useProductInputs(apiUrl, groupedProductId, {
		fetcher,
		onSuccess: async (successData: Record<string, SimpleProduct> | undefined) => {
			const successProducts =
				successData !== undefined
					? Object.entries(successData)
							.map(([id, product]) => ({ ...product, id }))
							.sort((a, b) => a.thickness - b.thickness)
					: undefined;

			const newProductId =
				successProducts?.find((item) => item.id === defaultSimpleProductId)?.id ??
				successProducts?.[0]?.id;

			if (newProductId) {
				form.setValue("simple_product_id", newProductId, { shouldValidate: true });

				const currentProduct = successProducts?.find((p) => p.id === newProductId);
				const units = currentProduct?.available_production_units;

				const newUnit = units?.length
					? (units?.find((u) => u === ProductionUnit.CompletePanel) ?? ProductionUnit.M2)
					: undefined;

				if (newUnit) {
					form.setValue("production_type", newUnit, { shouldValidate: true });
				}
			}
		},
	});

	const products =
		data !== undefined
			? Object.entries(data)
					.map(([id, product]) => ({ ...product, id }))
					.sort((a, b) => a.thickness - b.thickness)
			: undefined;

	return { products, isLoading };
}

interface SimpleProductDataProps {
	apiUrl: string;
	groupedProductId: string;
	simpleProductId?: string;
}

export function getSimpleProductData(props: SimpleProductDataProps) {
	const { apiUrl, groupedProductId, simpleProductId } = props;

	const { products } = getSimpleProductsData({ apiUrl, groupedProductId });

	const currentProduct = products?.find((p) => p.id === simpleProductId);
	const units = currentProduct?.available_production_units;

	return { currentProduct, units };
}

interface FinishInputsProps {
	apiUrl: string;
	simpleProductId: string;
}

export function getFinishesData(props: FinishInputsProps) {
	const { apiUrl, simpleProductId } = props;

	const { data, isLoading } = useFinishInputs(apiUrl, simpleProductId, "plate_rectangle_custom", {
		fetcher,
	});

	const finishes = useMemo(() => {
		if (data) {
			type ValuesType = { value: string | number; label: string }[];
			type FieldType = { label: string; values?: ValuesType };
			const availableSidesMap = new Map<string, string>();
			const availableOptionsMap = new Map<string, { label: string; field?: FieldType }>();

			for (const finishKey in data) {
				const finish = data[finishKey];

				if (!finish) continue;

				// Collect available sides
				for (const side of finish.availableOnSides) {
					availableSidesMap.set(side.sideCode, side.sideName);
				}

				// Collect available options
				for (const optionKey in finish.options) {
					const option = finish.options[optionKey];
					if (!option) continue;

					let field: FieldType | undefined;
					if (option.constraints.fields) {
						const fieldValues = Object.values(option.constraints.fields);
						if (fieldValues.length > 0) {
							const firstField = fieldValues[0];
							if (!firstField) continue;

							field = { label: firstField.label, values: firstField.values || [] };
						}
					}

					availableOptionsMap.set(option.id, { label: option.label, field });
				}
			}

			const availableSides = [...availableSidesMap.entries()].map(([code, label]) => {
				return { code: code as SidesType, label };
			});

			const availableOptions = [...availableOptionsMap.entries()].map(
				([code, { label, field }]) => ({ code: code as FinishesType, label, field }),
			);

			return { availableSides, availableOptions };
		}
	}, [data]);

	return { finishes, isLoading };
}

export type FinishesDataType = ReturnType<typeof getFinishesData>;
